import React, { useEffect, useState } from "react"
import { Button } from "react-bootstrap"
import html2canvas from "html2canvas"

import DataExportButton from "../DataExportButton"
import { adjustDateValue, getCurrentTime, isNumber, stringifyDate } from "../../../utils"
import { exportCSVFromArray } from "../../data_export/ObservationExporter"
import { registration, borderStyle } from "./utils"
import PageLoadingPanel from "../../../widgets/PageLoadingPanel"

export default function ReportGenerationTable({
  intl,
  isLoading = false,
  data = {},
  selectedRuns = [],
  timestampCount = 0,
  selectedMetrics = [],
  runDetails = [],
  averageMinutes = 60,
  getRunTimestamps,
  getRun,
}) {
  const defaultTagCols = 2
  const [colsPerRun, setColsPerRun] = useState(4)
  const [tagCols, setTagCols] = useState(defaultTagCols)
  const [tsCols, setTSCols] = useState(6)
  const [dataRows, setDataRows] = useState([])
  const [singleTsHeader, setSingleTsHeader] = useState("")
  const [runHeader, setRunHeader] = useState([])
  const [dataExportRows, setExportDataRows] = useState([])
  useEffect(() => {
    if (!isLoading) {
      // Figure out columns and spacing in rendered table
      const tagInfoCols = defaultTagCols
      const availableCols = 12
      const dataColCount = availableCols - tagInfoCols
      const runCols = Math.floor(dataColCount / selectedRuns.length)
      setColsPerRun(runCols)
      setTagCols(tagInfoCols + (dataColCount - runCols * selectedRuns.length))
      setTSCols(Math.floor(availableCols / timestampCount))

      // Organize data for UI display and CSV Export
      const headerRow = [`${registration.averaged_over_minutes(intl)}: ${averageMinutes}`]
      let uniqueTimestamps = new Set()
      const rows = []
      const runEntries = []
      selectedMetrics.forEach((metric, tag_idx) => {
        const { label, tag, units, min = "", max = "" } = metric
        const tagLabel = label || tag
        const row = [[tagLabel, tagLabel !== tag ? tag : "", units || "-", min, max]]
        selectedRuns.forEach((run_id) => {
          const first = tag_idx === 0
          const runInfo = getRun(run_id)
          if (first) {
            runEntries.push([
              runInfo.reactor ? `${runInfo.reactor} (${runInfo.name})` : "-",
              ...runDetails.map((d) => `${d[1]}: ${d[2](runInfo)}`),
            ])
          }
          const runRow = []
          const runHeaderRow = []
          getRunTimestamps(run_id).forEach((ts) => {
            first && runHeaderRow.push(`${stringifyDate(ts, true)}__${runInfo.reactor || "-"}`)
            uniqueTimestamps.add(ts)
            const val = data[run_id]?.[tag]?.[ts]?.["value"]
            runRow.push(`${val ? val.toFixed(2) : "x"}`)
          })
          row.push(runRow)
          first && headerRow.push(runHeaderRow)
        })
        rows.push(row)
      })

      uniqueTimestamps = [...uniqueTimestamps]
      setSingleTsHeader(
        uniqueTimestamps.length === 1
          ? `${stringifyDate(adjustDateValue(uniqueTimestamps[0], "minute", averageMinutes, false))} - ${
              uniqueTimestamps[0]
            }`
          : "",
      )

      const finalData = [headerRow, ...rows]

      // Data for UI display
      setRunHeader(runEntries)
      setDataRows(uniqueTimestamps.length === 1 ? rows : finalData)

      // Slight adjustment to data structure for CSV export
      setExportDataRows(
        finalData.map((d) => {
          const exportArr = [d[0]]
          d.forEach((entry, idx) => idx > 0 && entry.forEach((item) => exportArr.push(item)))
          return exportArr
        }),
      )
    }
  }, [isLoading, data])

  const [imageDownloading, setImageDownloading] = useState(false)
  const handleImgDownload = (e) => {
    e.stopPropagation()

    // Function to export a PNG image of the HTML element labelled id=report-table
    // Outputs the table from the UI
    const saveAs = (uri, filename) => {
      const element = document.getElementById("shift-report-modal")
      const link = document.createElement("a")

      if (typeof link.download === "string") {
        link.href = uri
        link.download = filename
        element.appendChild(link) //Firefox requires the link to be in the body
        link.click() // Simulate click
        element.removeChild(link) // Remove the link when done
      } else {
        window.open(uri)
      }
      setImageDownloading(false)
    }

    html2canvas(document.getElementById("report-table")).then((canvas) => {
      saveAs(canvas.toDataURL(), `ShiftReport_${stringifyDate(getCurrentTime(), true)}.png`)
    })
  }

  return (
    <>
      {isLoading && <PageLoadingPanel />}
      {Object.keys(data).length > 0 && (
        <>
          <div id={"info-accessor"}>
            <Button
              variant={"outline-primary"}
              onClick={(e) => {
                setImageDownloading(true)
                handleImgDownload(e)
              }}
              disabled={isLoading || Object.keys(data).length === 0 || imageDownloading}
              title={registration.download_table_image(intl)}
            >
              {imageDownloading ? (
                <div
                  className="spinner-border text-primary text-center"
                  style={{ width: "15px", height: "15px" }}
                  role="status"
                />
              ) : (
                <span className={"fa fa-picture-o"} />
              )}
            </Button>
            <DataExportButton
              intl={intl}
              filename={`ShiftReport_${stringifyDate(getCurrentTime(), true)}.csv`}
              disabled={isLoading || Object.keys(data).length === 0}
              dataThunk={exportCSVFromArray.bind(true, dataExportRows)}
            />
          </div>
          <div id={"report-table"} style={{ marginTop: "15px", fontSize: "90%" }}>
            <div
              className={"row"}
              style={{
                margin: "0px",
                backgroundColor: "var(--lanzalake-blue-primary)",
                fontSize: "90%",
              }}
            >
              <div className={`col-${tagCols} text-center`}>
                <div style={{ fontSize: "85%" }} className={"text-white"}>
                  {singleTsHeader}
                </div>
              </div>
              {runHeader.map((runInfo, run_idx) => {
                return (
                  <div
                    className={`col-${colsPerRun}`}
                    key={`runHeader${run_idx}`}
                    style={{ ...borderStyle, borderBottom: "none" }}
                  >
                    {runInfo.map((info, detail_idx) => (
                      <div key={`runHeader${run_idx}_detail${detail_idx}`}>
                        <p className={"text-center text-white"} style={{ margin: "0px" }}>
                          {info}
                        </p>
                      </div>
                    ))}
                  </div>
                )
              })}
            </div>
            {dataRows.map((row, row_idx) => {
              const isHeaderRow = row_idx === 0 && !singleTsHeader
              return (
                <div
                  className={"row"}
                  key={`row${row_idx}`}
                  style={
                    isHeaderRow
                      ? {
                          backgroundColor: "var(--lanzalake-blue-primary)",
                          margin: "0",
                        }
                      : { margin: "0" }
                  }
                >
                  {row.map((column, col_idx) => {
                    const extraStyle = col_idx === 0 && isHeaderRow ? { padding: "3px" } : {}
                    return (
                      <div
                        key={`row${row_idx}_col${col_idx}`}
                        style={{ ...borderStyle, ...extraStyle }}
                        className={`col-${col_idx === 0 ? tagCols : colsPerRun}`}
                      >
                        {col_idx === 0 ? (
                          <div
                            className={isHeaderRow ? "text-end text-white" : ""}
                            style={{ fontSize: "85%", margin: "0" }}
                          >
                            {isHeaderRow ? (
                              <div>{column}</div>
                            ) : (
                              <>
                                <div className={"row"}>
                                  <div className={"col-9"} style={{ padding: "0 0 0 5px" }}>
                                    <b>{column[0]}</b>
                                  </div>
                                  <div
                                    className={"col-3 text-end"}
                                    style={{ fontSize: "80%", fontStyle: "italic", padding: "0 5px 0 0" }}
                                  >
                                    {column[2]}
                                  </div>
                                </div>
                                {column[1] && (
                                  <div className={"row"}>
                                    <div
                                      className={"col-12"}
                                      style={{
                                        fontSize: "80%",
                                        fontStyle: "italic",
                                        padding: "0 0 0 5px",
                                      }}
                                    >
                                      <p style={{ margin: "0" }}>[{column[1]}]</p>
                                    </div>
                                  </div>
                                )}
                                {(column[3] || column[4]) && (
                                  <div className={"row"}>
                                    <div
                                      className={"col-12"}
                                      style={{
                                        fontSize: "80%",
                                        fontStyle: "italic",
                                        padding: "0 0 0 5px",
                                        display: "flex",
                                      }}
                                    >
                                      {(column[3] || column[4]) && (
                                        <p style={{ margin: "0" }}>
                                          [
                                          {isNumber(column[3]) && (
                                            <span>{`${registration.min_label(intl)}: ${column[3]}`}</span>
                                          )}
                                          {isNumber(column[3]) && isNumber(column[4]) && (
                                            <span>&nbsp;-&nbsp;</span>
                                          )}
                                          {isNumber(column[4]) && (
                                            <span>{`${registration.max_label(intl)}: ${column[4]}`}</span>
                                          )}
                                          ]
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        ) : (
                          <div className={"row"}>
                            {column.map((e, entry_idx) => {
                              const info = e ? e.split("__")[0].split(" ") : ["-"]
                              return (
                                <div
                                  key={`row${row_idx}_col${col_idx}_entry${entry_idx}`}
                                  className={`col-${tsCols}`}
                                  style={{ padding: "0" }}
                                >
                                  <div className={"text-center"}>
                                    {info.map((r, i) => (
                                      <p
                                        className={isHeaderRow ? "text-white" : ""}
                                        key={`row${row_idx}_col${col_idx}_entry${entry_idx}_${i}`}
                                        style={{ fontSize: "85%", margin: "0" }}
                                      >
                                        {r}
                                      </p>
                                    ))}
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )
            })}
          </div>
        </>
      )}
    </>
  )
}
